import { useCallback, useEffect, useState } from "react";

import { isAPIError } from "@/core/services";
import { listCargaTrabalhoProfissionais } from "@/modules/carga-trabalho";
import type { CargaTrabalhoPeriodo, CargaTrabalhoProfissional } from "@/modules/carga-trabalho";
import {
  CALENDARIO_FORBIDDEN_MESSAGE,
  CALENDARIO_LOAD_ERROR_MESSAGE,
} from "../../equipe-obra.constants";

/**
 * Alocações e indisponibilidades dos profissionais da equipe no período da obra, pelo
 * mesmo endpoint da Carga de Trabalho de Profissionais (HU 1779), filtrado por
 * `profissionalIds`. Quem usa ainda recorta pela equipe em edição.
 */
export async function carregarAlocacoesDoPeriodo(
  periodo: CargaTrabalhoPeriodo,
  profissionalIds: string[],
  signal?: AbortSignal,
): Promise<CargaTrabalhoProfissional[]> {
  const data = await listCargaTrabalhoProfissionais({ periodo, profissionalIds, signal });
  return data.profissionais;
}

export type CalendarioAlocacaoStatus = "idle" | "loading" | "success" | "error" | "forbidden";

export interface CalendarioAlocacaoState {
  status: CalendarioAlocacaoStatus;
  profissionais: CargaTrabalhoProfissional[];
  errorMessage: string | null;
}

const IDLE: CalendarioAlocacaoState = { status: "idle", profissionais: [], errorMessage: null };

/** Carrega quando há profissionais na equipe, quando ela muda e a cada `reload`. */
export function useCalendarioAlocacao({
  periodo,
  profissionalIds,
}: {
  periodo: CargaTrabalhoPeriodo;
  profissionalIds: string[];
}): CalendarioAlocacaoState & { reload: () => void } {
  const [state, setState] = useState<CalendarioAlocacaoState>(IDLE);
  const [reloadToken, setReloadToken] = useState(0);
  const { inicio, fim } = periodo;
  // Chave estável: a lista chega recriada a cada render da aba.
  const idsKey = [...new Set(profissionalIds)].sort().join("|");

  useEffect(() => {
    if (!idsKey) return;
    let cancelled = false;
    const controller = new AbortController();
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    carregarAlocacoesDoPeriodo({ inicio, fim }, idsKey.split("|"), controller.signal)
      .then((profissionais) => {
        if (!cancelled) setState({ status: "success", profissionais, errorMessage: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const forbidden = isAPIError(error) && error.status === 403;
        setState({
          status: forbidden ? "forbidden" : "error",
          profissionais: [],
          errorMessage: forbidden ? CALENDARIO_FORBIDDEN_MESSAGE : CALENDARIO_LOAD_ERROR_MESSAGE,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [idsKey, inicio, fim, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
